import { Page } from '@/app/components/page';
import { Header } from '@/app/components/header';
import { faqHeader } from '@/misc/images';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useUserInfo } from '@/context/user-info';
import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronRight } from '@untitled-ui/icons-react';
import { useQuery } from 'react-query';
import { getFaqData } from '@/services/app.service';
import { useFaqData } from '@/context/faq-data.provider';
import faqJson from '@/lib/seo-json/faq.json';

export function HelpcenterLayout() {
  const { t } = useTranslation();
  const { userLanguage } = useUserInfo();
  const { setCollections } = useFaqData();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: faqCollections } = useQuery({
    queryKey: [getFaqData.name],
    queryFn: getFaqData,
    onSuccess: (data) => {
      setCollections(data);
    },
  });

  const selectedCollection = useMemo(
    () => location.pathname ?? faqCollections?.[0].slug,
    [location.pathname, faqCollections],
  );

  useEffect(() => {
    if (
      (faqCollections?.length ?? 0) > 0 &&
      (location.pathname === `/${userLanguage}/faq` ||
        location.pathname === `/${userLanguage}/faq/`)
    ) {
      navigate(`/${userLanguage}/faq/${faqCollections?.[0].slug}`, { replace: true });
    }
  }, [
    location.pathname,
    userLanguage,
    navigate,
    faqCollections?.length,
    faqCollections?.[0]?.slug,
  ]);

  return (
    <Page
      title={t('translation:seo.faq.title')}
      description={t('translation:seo.faq.description')}
      keywords={t('translation:seo.faq.keywords')}
      jsonLd={faqJson}
    >
      <Header
        ariaLabel={t('translation:accessibility.aria_label.header.faq')}
        image={faqHeader}
        mobileImage={faqHeader}
        imageAlt={t('translation:accessibility.image.alt.faq_header')}
      >
        <div className="absolute z-20 flex w-full h-full flex-col items-center justify-center text-center gap-12 pointer-events-auto">
          <div className="w-full flex flex-col gap-4 py-8 px-2.5 md:gap-2">
            <h1 className="font-phosphate text-5xl leading-[60px] tracking-[3px] text-white">
              {t('translation:faq.header.title')}
            </h1>
            <h2 className="px-4 text-xl font-medium leading-8 text-white">
              {t('translation:faq.header.subtitle')}
            </h2>
          </div>
        </div>
      </Header>
      <main className="flex flex-1 justify-center overflow-auto">
        <div className="w-full max-w-[1550px] flex flex-col justify-center gap-24 p-6 sm:p-12 md:flex-row">
          <div className="w-full h-fit flex flex-col gap-6 bg-bg-dark p-6 sm:p-10 md:w-80">
            <h2 className="font-phosphate text-white text-3xl">
              {t('translation:faq.table_of_contents')}
            </h2>
            <ul className="flex flex-col gap-1">
              {faqCollections?.map((collection) => (
                <li key={collection.slug} className="flex items-center gap-1">
                  {selectedCollection.includes(collection.slug) && (
                    <ChevronRight className="size-4 text-white" />
                  )}
                  <Link
                    className={cn({
                      'uppercase text-fg-secondary': true,
                      'font-bold': selectedCollection.includes(collection.slug),
                    })}
                    to={`/${userLanguage}/faq/${collection.slug}`}
                  >
                    {collection.translated_content[userLanguage]?.name ??
                      collection.translated_content.en.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <Outlet />
        </div>
      </main>
    </Page>
  );
}
